import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  automationService,
  type Strategy,
  type RuleStatistics,
  type StrategyTrade,
} from '@/services/strategyService';
import { scannerApiService } from '@/services/scannerService';
import { RuleFormDialog } from './RuleFormDialog';
import { SpecHistoryDialog } from './SpecHistoryDialog';
import { EquityCurveChart, PnLDistributionChart, MfeScatterPlot } from './PerformanceCharts';
import {
  Plus,
  Play,
  Pause,
  Trash2,
  Edit,
  History,
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  RefreshCw,
} from 'lucide-react';

// ── Helpers ────────────────────────────────────────────────

const fmt = (n: number, digits = 2) => n.toFixed(digits);
const fmtPct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
const fmtDollar = (n: number) => `${n >= 0 ? '+' : ''}$${n.toFixed(2)}`;
const fmtDuration = (secs: number) => {
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
};

// ── Main Page Component ────────────────────────────────────

export const StrategiesPage: React.FC = () => {
  const [rules, setRules] = useState<Strategy[]>([]);
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [stats, setStats] = useState<Record<string, RuleStatistics>>({});
  const [trades, setTrades] = useState<Record<string, StrategyTrade[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scannerNames, setScannerNames] = useState<Record<string, string>>({});
  const [formOpen, setFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<Strategy | null>(null);
  const [historyRule, setHistoryRule] = useState<Strategy | null>(null);

  const loadRules = useCallback(async () => {
    try {
      setLoading(true);
      const data = await automationService.getRules();
      setRules(data);

      // Load scanner names for display
      const scanners = await scannerApiService.getScanners();
      const nameMap: Record<string, string> = {};
      scanners.forEach(s => { nameMap[s.id] = s.name; });
      setScannerNames(nameMap);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRules(); }, [loadRules]);

  const loadStats = useCallback(async (ruleId: string) => {
    try {
      const [s, t] = await Promise.all([
        automationService.getRuleStatistics(ruleId),
        automationService.getRuleTrades(ruleId, 50),
      ]);
      setStats(prev => ({ ...prev, [ruleId]: s }));
      setTrades(prev => ({ ...prev, [ruleId]: t }));
    } catch (e) {
      console.error('Failed to load stats', e);
    }
  }, []);

  useEffect(() => {
    if (selectedRuleId) loadStats(selectedRuleId);
  }, [selectedRuleId, loadStats]);

  const toggleRule = async (rule: Strategy) => {
    try {
      if (rule.enabled) {
        await automationService.disableRule(rule.id);
      } else {
        await automationService.enableRule(rule.id);
      }
      loadRules();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const deleteRule = async (ruleId: string) => {
    if (!confirm('Delete this rule?')) return;
    try {
      await automationService.deleteRule(ruleId);
      if (selectedRuleId === ruleId) setSelectedRuleId(null);
      loadRules();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const openNewRule = () => {
    setEditingRule(null);
    setFormOpen(true);
  };

  const openEditRule = (rule: Strategy) => {
    setEditingRule(rule);
    setFormOpen(true);
  };

  const selectedRule = rules.find(r => r.id === selectedRuleId);
  const selectedStats = selectedRuleId ? stats[selectedRuleId] : null;
  const selectedTrades = selectedRuleId ? trades[selectedRuleId] : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Target className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Automation Rules</h2>
            <span className="text-xs text-muted-foreground">Scanner-triggered paper trading</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadRules}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={openNewRule}>
            <Plus className="h-3.5 w-3.5 mr-1" /> New Rule
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">{error}</div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* ── Rule List ── */}
        <div className="space-y-3">
          {loading && <div className="text-muted-foreground text-sm">Loading rules...</div>}
          {rules.map(rule => (
            <Card
              key={rule.id}
              className={`cursor-pointer transition-colors ${selectedRuleId === rule.id ? 'border-primary' : 'hover:border-muted-foreground/30'}`}
              onClick={() => setSelectedRuleId(rule.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{rule.name}</span>
                    <Badge variant={rule.enabled ? 'default' : 'secondary'} className="text-[10px]">
                      {rule.enabled ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={e => { e.stopPropagation(); openEditRule(rule); }}
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      title="Version history"
                      onClick={e => { e.stopPropagation(); setHistoryRule(rule); }}
                    >
                      <History className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={e => { e.stopPropagation(); toggleRule(rule); }}
                    >
                      {rule.enabled ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-destructive"
                      onClick={e => { e.stopPropagation(); deleteRule(rule.id); }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>Scanner: {scannerNames[rule.scannerId] || rule.scannerId}</div>
                  <div>Direction: {rule.direction} | Max: {rule.maxConcurrentTrades} trades</div>
                  <div>Sizing: {rule.sizing.mode} {rule.sizing.value}{rule.sizing.mode === 'RiskPercent' ? '%' : ''}</div>
                  <div>Stop: {rule.exits.stopLoss?.mode} ×{rule.exits.stopLoss?.value} | Trail: {rule.exits.trailing?.mode} {rule.exits.trailing?.value}%</div>
                </div>
              </CardContent>
            </Card>
          ))}
          {!loading && rules.length === 0 && (
            <div className="text-center text-muted-foreground text-sm py-8">
              No automation rules yet. Click "New Rule" to create one.
            </div>
          )}
        </div>

        {/* ── Stats Panel ── */}
        <div className="xl:col-span-2 space-y-4">
          {selectedRule && selectedStats ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Total Trades" value={selectedStats.totalTrades.toString()} />
                <StatCard label="Win Rate" value={fmtPct(selectedStats.winRate)} positive={selectedStats.winRate >= 50} />
                <StatCard label="Total P&L" value={fmtDollar(selectedStats.totalPnL)} positive={selectedStats.totalPnL >= 0} />
                <StatCard label="Profit Factor" value={fmt(selectedStats.profitFactor)} positive={selectedStats.profitFactor >= 1} />
                <StatCard label="Expectancy" value={fmtDollar(selectedStats.expectancy)} positive={selectedStats.expectancy >= 0} />
                <StatCard label="Avg R-Multiple" value={fmt(selectedStats.avgRMultiple)} positive={selectedStats.avgRMultiple >= 0} />
                <StatCard label="Max Drawdown" value={fmtDollar(-selectedStats.maxDrawdown)} positive={false} />
                <StatCard label="Avg Hold Time" value={fmtDuration(selectedStats.avgHoldingSeconds)} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Card>
                  <CardHeader className="p-3 pb-1">
                    <CardTitle className="text-sm">Best & Worst</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-1 text-xs space-y-1">
                    <div className="text-green-500">Largest Win: {fmtDollar(selectedStats.largestWin)}</div>
                    <div className="text-green-500">Avg Win: {fmtDollar(selectedStats.avgWin)}</div>
                    <div className="text-red-500">Largest Loss: {fmtDollar(selectedStats.largestLoss)}</div>
                    <div className="text-red-500">Avg Loss: {fmtDollar(selectedStats.avgLoss)}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="p-3 pb-1">
                    <CardTitle className="text-sm">Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-1 text-xs space-y-1">
                    <div className="text-green-500">Winners: {selectedStats.winners}</div>
                    <div className="text-red-500">Losers: {selectedStats.losers}</div>
                    <div>Breakeven: {selectedStats.breakeven}</div>
                    <div>Open: {selectedStats.openTrades}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Equity Curve */}
              {selectedStats.equityCurve.length > 0 && (
                <EquityCurveChart equityCurve={selectedStats.equityCurve} />
              )}

              {/* P&L Distribution + MFE/MAE */}
              {selectedTrades && selectedTrades.length > 0 && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                  <PnLDistributionChart trades={selectedTrades.filter(t => t.status === 'Closed')} />
                  <MfeScatterPlot trades={selectedTrades.filter(t => t.status === 'Closed')} />
                </div>
              )}

              {/* Trade table */}
              {selectedTrades && selectedTrades.length > 0 && (
                <Card>
                  <CardHeader className="p-3 pb-1">
                    <CardTitle className="text-sm">Recent Trades ({selectedTrades.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-auto max-h-[400px]">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/40 sticky top-0">
                          <tr>
                            <th className="p-2 text-left">Symbol</th>
                            <th className="p-2 text-left">Dir</th>
                            <th className="p-2 text-right">Entry</th>
                            <th className="p-2 text-right">Exit</th>
                            <th className="p-2 text-right">P&L</th>
                            <th className="p-2 text-right">R</th>
                            <th className="p-2 text-left">Reason</th>
                            <th className="p-2 text-right">Hold</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedTrades.map(t => (
                            <tr key={t.id} className="border-b border-muted/20 hover:bg-muted/10">
                              <td className="p-2 font-medium">{t.symbol}</td>
                              <td className="p-2">{t.direction === 'Long' ? '▲' : '▼'}</td>
                              <td className="p-2 text-right">${fmt(t.entryPrice)}</td>
                              <td className="p-2 text-right">{t.exitPrice ? `$${fmt(t.exitPrice)}` : '—'}</td>
                              <td className={`p-2 text-right ${t.realizedPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {t.status === 'Closed' ? fmtDollar(t.realizedPnL) : '—'}
                              </td>
                              <td className="p-2 text-right">{t.status === 'Closed' ? fmt(t.realizedRMultiple, 1) : '—'}</td>
                              <td className="p-2">{t.exitReason || (t.status === 'Open' ? 'Open' : '—')}</td>
                              <td className="p-2 text-right">{t.holdingSeconds > 0 ? fmtDuration(t.holdingSeconds) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : selectedRule && !selectedStats ? (
            <div className="text-center text-muted-foreground text-sm py-8">
              Loading statistics...
            </div>
          ) : (
            <div className="text-center text-muted-foreground text-sm py-8">
              Select a rule to view performance statistics
            </div>
          )}
        </div>
      </div>

      <RuleFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editRule={editingRule}
        onSaved={loadRules}
      />

      {historyRule && (
        <SpecHistoryDialog
          open={!!historyRule}
          onOpenChange={open => { if (!open) setHistoryRule(null); }}
          ruleId={historyRule.id}
          ruleName={historyRule.name}
        />
      )}
    </div>
  );
};

// ── Stat Card ──────────────────────────────────────────────

const StatCard: React.FC<{ label: string; value: string; positive?: boolean }> = ({ label, value, positive }) => (
  <Card>
    <CardContent className="p-3">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-lg font-bold ${positive === true ? 'text-green-500' : positive === false ? 'text-red-500' : ''}`}>
        {value}
      </div>
    </CardContent>
  </Card>
);
